import { useChartSeries } from '../ChartSeriesContext'
import { formatChartValue } from '../chartFormatting'

import { buildBreakdownRows } from './buildBreakdownRows'
import { formatSignedPercent, formatSignedValue } from './formatBreakdown'

import type { JSX } from 'react'

/** Signed percent, or an em-dash when there's no baseline (started at zero). */
function formatPct(pct: number | null): string {
  return pct == null ? '—' : formatSignedPercent(pct)
}

interface BreakdownDataTableProps {
  /** Names the table (the chart's accessible label). */
  caption: string
}

/**
 * A screen-reader data alternative for the breakdown bar chart, rendered inside
 * {@link ChartFrame}'s visually-hidden disclosure. Reads the series registry from
 * context (same source, and same ranking, as the bars) so it can't drift: one row
 * per channel with its start, change, current value, and percent change as text —
 * the split the SVG bars can't announce on their own.
 */
export function BreakdownDataTable({
  caption,
}: BreakdownDataTableProps): JSX.Element | null {
  const series = useChartSeries()
  const rows = buildBreakdownRows(series)

  if (rows.length === 0) return null

  const seriesById = new Map(series.map((s) => [s.id, s]))
  const summary = rows
    .map((r) => {
      const s = seriesById.get(r.id)
      return `${s?.label ?? r.id}: ${formatChartValue(r.end, s?.unit)} (${formatSignedValue(r.delta, s?.unit)})`
    })
    .join('. ')

  return (
    <table>
      <caption>{`${caption}. ${summary}`}</caption>
      <thead>
        <tr>
          <th scope="col">Channel</th>
          <th scope="col">At start</th>
          <th scope="col">Change</th>
          <th scope="col">Now</th>
          <th scope="col">% change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const s = seriesById.get(r.id)
          return (
            <tr key={r.id}>
              <th scope="row">{s?.label ?? r.id}</th>
              <td>{formatChartValue(r.start, s?.unit)}</td>
              <td>{formatSignedValue(r.delta, s?.unit)}</td>
              <td>{formatChartValue(r.end, s?.unit)}</td>
              <td>{formatPct(r.pct)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
